import React from 'react';
import { Box, ToggleButton, ToggleButtonGroup, IconButton, Tooltip, Typography } from '@mui/material';
import UndoIcon from '@mui/icons-material/Undo';
import DeleteIcon from '@mui/icons-material/Delete';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import HorizontalRuleIcon from '@mui/icons-material/HorizontalRule';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

/**
 * Mode descriptions for the number line
 */
const MODE_INFO = {
  point: {
    label: 'Point',
    icon: <RadioButtonCheckedIcon />,
    description: 'Click to place a single point (e.g., x = 3)',
  },
  interval: {
    label: 'Interval',
    icon: <HorizontalRuleIcon />,
    description: 'Click two points to create an interval (e.g., -3 < x < 3)',
  },
  ray: {
    label: 'Ray',
    icon: <ArrowForwardIcon />,
    description: 'Click origin, then direction to create a ray (e.g., x > 3)',
  },
};

/**
 * Toolbar for number line mode selection
 */
const NumberLineControls = ({
  mode,
  onModeChange,
  allowedModes = ['point', 'interval', 'ray'],
  canUndo = false,
  onUndo,
  onClear,
  pendingValue = null,
  onCancelOperation,
  disabled = false,
  compact = false,
}) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: compact ? 'row' : 'column',
        gap: 1,
        mb: 2,
      }}
    >
      {/* Mode selection */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ToggleButtonGroup
          value={mode}
          exclusive
          onChange={(_, newMode) => {
            if (newMode && !disabled) {
              onModeChange(newMode);
              if (onCancelOperation) {
                onCancelOperation();
              }
            }
          }}
          size="small"
          disabled={disabled}
        >
          {allowedModes.map((m) => (
            <ToggleButton
              key={m}
              value={m}
              sx={{
                px: 2,
                textTransform: 'capitalize',
              }}
            >
              <Tooltip title={MODE_INFO[m]?.description || m}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {MODE_INFO[m]?.icon}
                  {!compact && (
                    <Typography variant="body2" sx={{ ml: 0.5 }}>
                      {MODE_INFO[m]?.label || m}
                    </Typography>
                  )}
                </Box>
              </Tooltip>
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        {/* Action buttons */}
        <Box sx={{ display: 'flex', gap: 0.5, ml: 1 }}>
          <Tooltip title="Undo">
            <span>
              <IconButton
                onClick={onUndo}
                disabled={!canUndo || disabled}
                size="small"
              >
                <UndoIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Clear all">
            <span>
              <IconButton
                onClick={onClear}
                disabled={disabled}
                size="small"
                color="error"
              >
                <DeleteIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {/* Status/hint text */}
      {!compact && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {MODE_INFO[mode]?.description}
          </Typography>
          {pendingValue !== null && (
            <Typography
              variant="caption"
              sx={{ color: 'warning.main', fontWeight: 'bold' }}
            >
              (Click again to complete)
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

export default NumberLineControls;
